<template>
    <div>
        <!-- turn-key: the delivery stack, drawn as a schematic rather than an icon grid -->
        <div class="stack-wrap">
            <div class="stack-grid">
                <div class="stack-left">
                    <div class="lane-eyebrow">Turn-key</div>
                    <h3 class="stack-h3">A line is where<br>most providers stop.</h3>
                    <p class="stack-p">We deliver the whole stack working — and then run it. One contract, one invoice, one team accountable for every layer below.</p>
                    <div class="pipeline">
                        <span class="pipe-pill pipe-blue">Design</span>
                        <span class="pipe-arrow">→</span>
                        <span class="pipe-pill pipe-blue">Install</span>
                        <span class="pipe-arrow">→</span>
                        <span class="pipe-pill pipe-hot">Run</span>
                    </div>
                    <div class="stack-hint">Select a layer →</div>
                </div>

                <div>
                    <div class="stack-head">
                        <span class="stack-head-k">The stack we run</span>
                        <span class="stack-head-k">Owned by us</span>
                    </div>
                    <div v-for="(s, i) in STACK" :key="s.n" class="stack-row-wrap" :class="{ open: store.layer === i }">
                        <button
                            class="stack-row ob-tap"
                            :aria-expanded="store.layer === i"
                            :aria-controls="`ob-stack-panel-${i}`"
                            @click="store.layer = i"
                        >
                            <span class="stack-row-flex">
                                <span class="stack-code" :class="{ open: store.layer === i }">{{ s.n }}</span>
                                <span class="stack-main">
                                    <span class="stack-title" :class="{ open: store.layer === i }">{{ s.t }}</span>
                                    <span class="stack-bar" :class="{ open: store.layer === i }" :style="{ width: s.w }" />
                                </span>
                                <span v-if="store.layer === i" class="stack-ours">Ours</span>
                            </span>
                        </button>
                        <div v-if="store.layer === i" :id="`ob-stack-panel-${i}`" role="region" class="stack-panel">{{ s.d }}</div>
                    </div>
                    <div class="stack-note">Buy any layer on its own, or hand us the whole stack. Either way it is scoped from a survey and quoted from a rate card.</div>
                </div>
            </div>
        </div>

        <!-- how we work: survey + KAM -->
        <div class="how">
            <div class="lane-eyebrow">How we work with you</div>
            <h3 class="how-h3">We come and look first. Then you get one person who owns it.</h3>
            <div class="how-grid">
                <div v-for="(w, i) in HOW_WE_WORK" :key="w.n">
                    <button
                        class="how-step ob-tap"
                        :aria-expanded="store.workStep === i"
                        :aria-controls="`ob-work-panel-${i}`"
                        @click="store.toggleWorkStep(i)"
                    >
                        <span class="how-step-top">
                            <span class="how-n">{{ w.n }}</span>
                            <span class="how-chev" :class="{ open: store.workStep === i }">⌄</span>
                        </span>
                        <span class="how-t">{{ w.t }}</span>
                        <span class="how-d">{{ w.d }}</span>
                    </button>
                    <div v-if="store.workStep === i" :id="`ob-work-panel-${i}`" role="region" class="how-panel">{{ w.detail }}</div>
                </div>
            </div>

            <div class="emph-grid">
                <div class="kam">
                    <div class="emph-head">
                        <div class="kam-icon">
                            <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                        </div>
                        <div class="kam-eyebrow">Dedicated to you</div>
                    </div>
                    <div class="kam-t">Your own key account manager.</div>
                    <p class="kam-p">One named person — not a queue, not a shared inbox. They know your sites, your history and your renewal date, run your service reviews and escalate on your behalf.</p>
                    <div class="kam-list">
                        <div class="kam-li"><span class="kam-check">✓</span><span>One direct number and email</span></div>
                        <div class="kam-li"><span class="kam-check">✓</span><span>Regular reviews with real figures</span></div>
                        <div class="kam-li"><span class="kam-check">✓</span><span>Plans upgrades before you outgrow them</span></div>
                    </div>
                </div>
                <div class="bizsup">
                    <div class="emph-head">
                        <div class="bizsup-icon">
                            <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" /></svg>
                        </div>
                        <div class="bizsup-eyebrow">Business only</div>
                    </div>
                    <div class="bizsup-t">A support team that only handles business.</div>
                    <p class="bizsup-p">Business faults go to engineers who work on business networks all day — never the consumer queue. Your tickets are prioritised ahead of residential traffic.</p>
                    <div class="kam-list">
                        <div class="bizsup-li"><span class="bizsup-check">✓</span><span>Separate business priority queue</span></div>
                        <div class="bizsup-li"><span class="bizsup-check">✓</span><span>Engineers who know your setup</span></div>
                        <div class="bizsup-li"><span class="bizsup-check">✓</span><span>Committed response and restoration targets</span></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- class ladder -->
        <div class="ladder">
            <div class="ladder-head">
                <h3 class="ladder-h3">Choose the promise,<br>not the megabits.</h3>
                <p class="ladder-p">One class choice sets fifteen attributes at once. The code travels on your proposal, so you can always see the rungs above you.</p>
            </div>
            <div class="ladder-card">
                <div class="ladder-tabs">
                    <button
                        v-for="code in classCodes"
                        :key="code"
                        class="ladder-tab"
                        :class="{ active: store.klass === code }"
                        @click="store.klass = code"
                    >
                        <span class="ladder-tab-code" :class="{ active: store.klass === code }">{{ code }}</span>
                        <span class="ladder-tab-name" :class="{ active: store.klass === code }">{{ CLASSES[code].name }}</span>
                    </button>
                </div>
                <div class="ladder-body">
                    <div class="ladder-title-row">
                        <span class="ladder-title">{{ activeClass.code }} · One Business {{ activeClass.name }}</span>
                        <span class="ladder-built">{{ activeClass.built }}</span>
                    </div>
                    <div class="ladder-rows">
                        <div v-for="[label, key] in CLASS_ROW_LABELS" :key="label" class="ladder-row">
                            <span class="ladder-row-k">{{ label }}</span>
                            <span class="ladder-row-v" :class="{ mandatory: String(activeClass[key]).includes('Mandatory') }">{{ activeClass[key] }}</span>
                        </div>
                    </div>
                    <div class="ladder-cta-row">
                        <a href="#contact" class="ladder-cta ob-press">Talk to us about {{ activeClass.code }} →</a>
                        <span class="ladder-cta-note">Quoted from a rate card against your sites and users — never improvised.</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="crosslink">
            <div>
                <div class="crosslink-t">Just need internet at home?</div>
                <div class="crosslink-d">Three simple tiers, first router free, flexible terms.</div>
            </div>
            <button class="crosslink-btn ob-press" @click="store.setMode('retail')">Switch to Home →</button>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'

    import { CLASSES, CLASS_ROW_LABELS, HOW_WE_WORK, STACK } from '@/data/content'
    import { useLandingStore } from '@/stores/landing'
    import type { ClassKey } from '@/types/Landing'

    const store = useLandingStore()

    const classCodes: ClassKey[] = ['L1', 'L2', 'L3', 'L4']
    const activeClass = computed(() => CLASSES[store.klass])
</script>

<style scoped>
    .lane-eyebrow { font-size: 12px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--eyebrow); }

    .stack-wrap { padding-bottom: 64px; }
    .stack-grid { display: grid; grid-template-columns: .86fr 1.14fr; gap: 56px; align-items: start; }
    .stack-left { position: sticky; top: 110px; }
    .stack-h3 { margin: 16px 0 0; font-size: clamp(26px, 3.2vw, 40px); font-weight: 700; letter-spacing: -.026em; line-height: 1.05; color: var(--ink); }
    .stack-p { margin: 18px 0 0; font-size: 16.5px; line-height: 1.6; color: var(--sub); }
    .pipeline { display: flex; align-items: center; gap: 8px; margin-top: 26px; }
    .pipe-pill { font-size: 12.5px; font-weight: 700; padding: 8px 14px; border-radius: 999px; color: var(--ob-white); }
    .pipe-blue { background: var(--ob-grad-brand-120); }
    .pipe-hot { background: var(--ob-grad-hot); }
    .pipe-arrow { color: var(--sub); font-size: 13px; }
    .stack-hint { margin-top: 28px; font-size: 13px; color: var(--sub); }

    .stack-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-bottom: 12px; border-bottom: 1px solid var(--hair); }
    .stack-head-k { font-size: 11px; font-weight: 700; letter-spacing: .16em; text-transform: uppercase; color: var(--sub); }
    .stack-row-wrap { border-bottom: 1px solid var(--hair2); background: transparent; transition: background .2s; }
    .stack-row-wrap.open { background: var(--surf); }
    .stack-row { display: block; width: 100%; cursor: pointer; border: none; background: transparent; padding: 22px 20px 22px 0; font-family: 'Poppins', sans-serif; }
    .stack-row-flex { display: flex; align-items: center; gap: 18px; }
    .stack-code { flex: none; width: 26px; font-size: 12px; font-weight: 700; letter-spacing: .08em; font-variant-numeric: tabular-nums; color: var(--sub); }
    .stack-code.open { color: var(--ob-sky); }
    .stack-main { flex: 1; min-width: 0; text-align: left; }
    .stack-title { display: block; font-size: clamp(18px, 2vw, 23px); font-weight: 700; letter-spacing: -.018em; color: var(--sub); }
    .stack-title.open { color: var(--ink); }
    .stack-bar { display: block; margin-top: 7px; height: 4px; border-radius: 3px; background: var(--ob-w-a28); }
    .stack-bar.open { background: var(--ob-grad-brand-120); }
    .stack-ours { flex: none; font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--ob-sky); }
    .stack-panel { padding: 0 20px 20px 44px; font-size: 14.5px; line-height: 1.6; color: var(--sub); }
    .stack-note {
        margin-top: 18px;
        padding: 18px 20px;
        border-radius: 14px;
        background: var(--surf);
        border: 1px solid var(--surfBorder);
        font-size: 14px;
        line-height: 1.55;
        color: var(--ink);
    }

    .how { padding: 44px 46px; border: 1px solid var(--surfBorder); border-radius: 26px; background: var(--surf); margin-bottom: 72px; }
    .how-h3 { margin: 16px 0 0; font-size: clamp(24px, 2.9vw, 36px); font-weight: 700; letter-spacing: -.024em; line-height: 1.06; color: var(--ink); max-width: 620px; }
    .how-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; margin-top: 36px; border-top: 1px solid var(--hair); }
    .how-step { display: block; width: 100%; cursor: pointer; border: none; background: transparent; padding: 26px 20px 26px 0; font-family: 'Poppins', sans-serif; border-radius: 14px; }
    .how-step-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
    .how-n {
        width: 34px;
        height: 34px;
        border-radius: 11px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 700;
        color: var(--ob-white);
        background: var(--ob-grad-brand);
    }
    .how-chev { font-size: 17px; color: var(--eyebrow); transition: transform .25s; }
    .how-chev.open { transform: rotate(180deg); }
    .how-t { display: block; margin-top: 16px; font-size: 17px; font-weight: 700; letter-spacing: -.012em; color: var(--ink); text-align: left; }
    .how-d { display: block; margin-top: 8px; font-size: 14px; line-height: 1.55; color: var(--sub); text-align: left; }
    .how-panel { margin: 0 20px 22px 0; padding-top: 14px; border-top: 1px solid var(--hair2); font-size: 13.5px; line-height: 1.55; color: var(--ink); }

    .emph-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 34px; padding-top: 32px; border-top: 1px solid var(--hair); }
    .emph-head { display: flex; align-items: center; gap: 13px; }
    .kam { padding: 30px 32px; border-radius: 20px; color: var(--ob-white); background: var(--ob-grad-brand-150); box-shadow: var(--ob-shadow-kam); }
    .kam-icon { width: 40px; height: 40px; border-radius: 12px; background: var(--ob-w-a20); display: flex; align-items: center; justify-content: center; color: var(--ob-white); }
    .kam-eyebrow { font-size: 11.5px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--ob-w-a90); }
    .kam-t { margin-top: 18px; font-size: clamp(20px, 2.2vw, 26px); font-weight: 700; letter-spacing: -.02em; line-height: 1.18; color: var(--ob-white); }
    .kam-p { margin: 12px 0 0; font-size: 15px; line-height: 1.6; color: var(--ob-w-a92); }
    .kam-list { display: grid; gap: 9px; margin-top: 20px; }
    .kam-li { display: flex; gap: 11px; align-items: baseline; font-size: 14.5px; color: var(--ob-w-a94); }
    .kam-check { font-weight: 700; }
    .bizsup { padding: 30px 32px; border-radius: 20px; border: 1px solid var(--surfBorder); background: var(--surf); }
    .bizsup-icon { width: 40px; height: 40px; border-radius: 12px; background: var(--ob-grad-brand); display: flex; align-items: center; justify-content: center; color: var(--ob-white); }
    .bizsup-eyebrow { font-size: 11.5px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--eyebrow); }
    .bizsup-t { margin-top: 18px; font-size: clamp(20px, 2.2vw, 26px); font-weight: 700; letter-spacing: -.02em; line-height: 1.18; color: var(--ink); }
    .bizsup-p { margin: 12px 0 0; font-size: 15px; line-height: 1.6; color: var(--sub); }
    .bizsup-li { display: flex; gap: 11px; align-items: baseline; font-size: 14.5px; color: var(--ink); }
    .bizsup-check { color: var(--ob-blue-deep); font-weight: 700; }
    .ob-corp .bizsup-check { color: var(--ob-sky-soft); }

    .ladder { margin-top: 72px; }
    .ladder-head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 32px; }
    .ladder-h3 { margin: 0; font-size: clamp(26px, 3.2vw, 40px); font-weight: 700; letter-spacing: -.026em; line-height: 1.04; color: var(--ink); }
    .ladder-p { margin: 0; max-width: 400px; font-size: 15.5px; line-height: 1.6; color: var(--sub); }
    .ladder-card { border: 1px solid var(--surfBorder); border-radius: 24px; overflow: hidden; background: var(--surf); }
    .ladder-tabs { display: flex; border-bottom: 1px solid var(--hair); }
    .ladder-tab {
        cursor: pointer;
        flex: 1;
        text-align: left;
        padding: 20px 22px;
        font-family: 'Poppins', sans-serif;
        transition: all .22s;
        border: none;
        border-left: 1px solid var(--hair2);
        background: transparent;
    }
    .ladder-tab.active { background: var(--ob-blue-a26); }
    .ladder-tab-code { font-size: 12px; font-weight: 700; letter-spacing: .14em; color: var(--sub); }
    .ladder-tab-code.active { color: var(--ob-sky-soft); }
    .ladder-tab-name { display: block; margin-top: 8px; font-size: 19px; font-weight: 700; letter-spacing: -.01em; color: var(--sub); }
    .ladder-tab-name.active { color: var(--ink); }
    .ladder-body { padding: 34px 36px; }
    .ladder-title-row { display: flex; flex-wrap: wrap; align-items: baseline; gap: 14px; margin-bottom: 24px; }
    .ladder-title { font-size: clamp(24px, 2.6vw, 34px); font-weight: 700; letter-spacing: -.022em; color: var(--ink); }
    .ladder-built { font-size: 14.5px; color: var(--sub); }
    .ladder-rows { display: grid; grid-template-columns: 1fr 1fr; gap: 0 44px; }
    .ladder-row { display: flex; align-items: baseline; justify-content: space-between; gap: 20px; padding: 13px 0; border-bottom: 1px solid var(--hair2); }
    .ladder-row-k { font-size: 14px; color: var(--sub); }
    .ladder-row-v { font-size: 14.5px; font-weight: 600; text-align: right; color: var(--ink); }
    .ladder-row-v.mandatory { color: var(--ob-sky-soft); }
    .ladder-cta-row { margin-top: 26px; display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
    .ladder-cta {
        background: var(--ob-blue);
        color: var(--ob-white);
        font-weight: 700;
        font-size: 15px;
        padding: 16px 30px;
        border-radius: 999px;
        box-shadow: var(--ob-shadow-glow-md);
        text-decoration: none;
    }
    .ladder-cta-note { font-size: 13.5px; color: var(--sub); }

    .crosslink {
        margin-top: 52px;
        padding: 26px 30px;
        border: 1px solid var(--surfBorder);
        border-radius: 20px;
        background: var(--surf);
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
    }
    .crosslink-t { font-size: 18px; font-weight: 700; color: var(--ink); }
    .crosslink-d { margin-top: 6px; font-size: 15px; color: var(--sub); }
    .crosslink-btn {
        border: none;
        background: var(--ob-blue);
        color: var(--ob-white);
        font-family: 'Poppins', sans-serif;
        font-weight: 700;
        font-size: 14.5px;
        padding: 14px 26px;
        border-radius: 999px;
        cursor: pointer;
        white-space: nowrap;
    }

    @media (max-width: 1024px) {
        .stack-grid { grid-template-columns: 1fr; gap: 36px; }
        .stack-left { position: static; }
        .how-grid { grid-template-columns: 1fr 1fr; }
        .emph-grid { grid-template-columns: 1fr; }
    }
    @media (max-width: 800px) {
        .ladder-rows { grid-template-columns: 1fr; }
    }
    @media (max-width: 700px) {
        .how { padding: 26px 22px; }
        .ladder-body { padding: 24px 20px; }
        .ladder-tabs { flex-wrap: wrap; }
        .ladder-tab { flex: 1 1 40%; padding: 14px 16px; }
        .kam, .bizsup { padding: 24px 22px; }
    }
    @media (max-width: 640px) {
        .how-grid { grid-template-columns: 1fr; }
        .how-step { padding: 20px 12px 20px 0; }
        .stack-panel { padding-left: 0; }
    }
</style>
